import { useEffect, useMemo, useState } from "react";
import * as LucideIcons from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";

type LucideIcon = React.FC<React.SVGProps<SVGSVGElement> & { size?: number }>;

export interface IconPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (iconName: string | null, color: string) => void;
  onRemove?: () => void;
  defaultIcon?: string | null;
  defaultColor?: string;
}

// Curated allowlist — broad enough for course themes, small enough to stay snappy
const ICON_NAMES: string[] = [
  // Education
  "Book", "BookOpen", "GraduationCap", "Pencil", "Highlighter", "School",
  "Library", "NotebookPen", "BookMarked", "ScrollText", "Languages",
  // Tech
  "Code2", "Cpu", "Database", "Terminal", "Laptop", "Smartphone",
  "Cloud", "Wifi", "Lock", "Shield", "Bug", "GitBranch",
  // Science
  "Atom", "FlaskConical", "Microscope", "Telescope", "Dna", "Brain",
  "TestTube", "Beaker", "Radiation", "Magnet",
  // Math
  "Calculator", "Sigma", "Pi", "Infinity", "Percent", "Divide",
  // Business
  "Briefcase", "TrendingUp", "BarChart3", "PieChart", "LineChart",
  "Building2", "Coins", "CreditCard", "Wallet", "Receipt",
  // Creative
  "Palette", "Brush", "Camera", "Music", "Film", "Mic", "Image",
  "Pen", "Sparkles", "Wand2",
  // General
  "Star", "Heart", "Flag", "Flame", "Zap", "Rocket", "Trophy",
  "Target", "Lightbulb", "Compass", "Map", "Globe", "Leaf",
  "Mountain", "Sun", "Moon", "Crown", "Gift", "Key", "Bell",
  "Tag", "Bookmark", "Folder", "Calendar", "Clock", "CheckCircle2",
];

const SWATCH_ROW_1 = [
  "#111111", "#ef4444", "#f97316", "#eab308", "#22c55e",
  "#3b82f6", "#a855f7", "#ec4899", "#6366f1", "#6b7280",
];
const SWATCH_ROW_2 = [
  "#14b8a6", "#06b6d4", "#10b981", "#8b5cf6", "#d946ef",
  "#f43f5e", "#f59e0b", "#84cc16", "#38bdf8", "#78716c",
];

function resolveIcon(name: string): LucideIcon | null {
  return (LucideIcons as unknown as Record<string, LucideIcon>)[name] ?? null;
}

const isValidHex = (v: string) => /^[0-9a-fA-F]{6}$/.test(v);

function Swatch({
  color,
  selected,
  onClick,
}: {
  color: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={color}
      onClick={onClick}
      className={cn(
        "aspect-square w-full rounded-full border-2 transition-transform hover:scale-110",
        selected ? "border-foreground" : "border-transparent",
      )}
      style={{ backgroundColor: color }}
    />
  );
}

function IconButton({
  name,
  color,
  selected,
  onClick,
}: {
  name: string;
  color: string;
  selected: boolean;
  onClick: () => void;
}) {
  const Icon = resolveIcon(name);
  if (!Icon) return null;
  return (
    <button
      type="button"
      title={name}
      aria-label={name}
      onClick={onClick}
      className={cn(
        "flex aspect-square w-full items-center justify-center rounded-md transition-colors hover:bg-muted",
        selected && "bg-muted ring-2 ring-ring",
      )}
    >
      <Icon size={18} strokeWidth={2} style={{ color }} aria-hidden="true" />
    </button>
  );
}

export function IconPickerDialog({
  open,
  onOpenChange,
  onConfirm,
  onRemove,
  defaultIcon = null,
  defaultColor = "#111111",
}: IconPickerDialogProps) {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const [selectedIcon, setSelectedIcon] = useState<string | null>(defaultIcon);
  const [selectedColor, setSelectedColor] = useState(defaultColor);
  const [hexInput, setHexInput] = useState(
    defaultColor.replace("#", "").toUpperCase(),
  );

  // Re-sync when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedIcon(defaultIcon);
      setSelectedColor(defaultColor);
      setHexInput(defaultColor.replace("#", "").toUpperCase());
      setQuery("");
    }
  }, [open, defaultIcon, defaultColor]);

  const filteredIcons = useMemo(() => {
    if (!query.trim()) return ICON_NAMES;
    const q = query.toLowerCase();
    return ICON_NAMES.filter((n) => n.toLowerCase().includes(q));
  }, [query]);

  function applyColor(hex: string) {
    setSelectedColor(hex);
    setHexInput(hex.replace("#", "").toUpperCase());
  }

  function handleHexChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
    setHexInput(val);
    if (isValidHex(val)) setSelectedColor("#" + val);
  }

  function handleHexBlur() {
    if (!isValidHex(hexInput)) {
      setHexInput(selectedColor.replace("#", "").toUpperCase());
    }
  }

  function handleConfirm() {
    onConfirm(selectedIcon, selectedColor);
    onOpenChange(false);
  }

  function handleRemove() {
    onRemove?.();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex w-[440px] flex-col gap-3 p-5 sm:max-w-[440px]">
        <DialogHeader className="space-y-0">
          <DialogTitle className="text-[15px] font-medium">
            {t("iconPicker.title")}
          </DialogTitle>
        </DialogHeader>

        <Input
          type="text"
          placeholder={t("iconPicker.search")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
          className="h-10"
        />

        <div className="scrollbar-hide h-[172px] overflow-y-auto overflow-x-hidden rounded-md border border-border bg-card p-1.5">
          {filteredIcons.length === 0 ? (
            <p className="py-10 text-center text-xs text-muted-foreground">
              {t("iconPicker.empty")}
            </p>
          ) : (
            <div className="grid grid-cols-8 gap-0.5">
              {filteredIcons.map((name) => (
                <IconButton
                  key={name}
                  name={name}
                  color={selectedColor}
                  selected={selectedIcon === name}
                  onClick={() => setSelectedIcon(name)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="rounded-md border border-border bg-card p-3">
          <div className="grid grid-cols-10 gap-1.5">
            {SWATCH_ROW_1.map((c) => (
              <Swatch
                key={c}
                color={c}
                selected={selectedColor.toLowerCase() === c.toLowerCase()}
                onClick={() => applyColor(c)}
              />
            ))}
          </div>
          <div className="mt-1.5 grid grid-cols-10 gap-1.5">
            {SWATCH_ROW_2.map((c) => (
              <Swatch
                key={c}
                color={c}
                selected={selectedColor.toLowerCase() === c.toLowerCase()}
                onClick={() => applyColor(c)}
              />
            ))}
          </div>

          <div className="mt-2.5 flex items-center gap-2">
            <div
              className="h-[30px] w-[30px] flex-shrink-0 rounded-full border border-border"
              style={{ backgroundColor: selectedColor }}
            />
            <Input
              type="text"
              value={hexInput}
              onChange={handleHexChange}
              onBlur={handleHexBlur}
              maxLength={6}
              spellCheck={false}
              aria-label="Hex color value"
              className="h-8 w-[110px] font-mono text-[13px] tracking-wide uppercase"
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRemove}
            className="text-muted-foreground hover:text-foreground"
          >
            {t("iconPicker.remove")}
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              {t("iconPicker.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleConfirm}
              disabled={!selectedIcon}
            >
              {t("iconPicker.confirm")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
